import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Grid, Typography, Box, Stack, Card, Button, Skeleton, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';

import Page from 'src/components/Page';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { studentService } from 'src/services/student.service';
import { savingsAccountService } from 'src/services/savingsAccount.service';
import { ruleEngineService } from 'src/services/ruleEngine.service';
import { transactionService } from 'src/services/transaction.service';
import { PendingTransactionsPanel, TransactionRulesManager, SavingsAccountManager } from 'src/components/student';

// Metric Card Component in unified Emerald Green & Slate design language
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: string;
  isLoading?: boolean;
}

function MetricCard({ title, value, subtitle, icon, isLoading }: MetricCardProps) {
  return (
    <Card
      elevation={0}
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0px 4px 12px rgba(15, 23, 42, 0.04)',
        },
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}
        >
          {title}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width={60} height={28} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', my: 0.25, fontSize: '1.25rem', letterSpacing: '-0.01em' }}>
            {value}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.75,
          bgcolor: 'rgba(5, 150, 105, 0.1)',
          color: '#059669',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon icon={icon} width={20} height={20} />
      </Box>
    </Card>
  );
}

export default function FinancialTools(): React.JSX.Element {
  const navigate = useNavigate();
  const { user, getBaseRoute } = useAuth();
  const [activeTab, setActiveTab] = useState<number>(0);

  // Get student ID from user
  const { data: studentId, isLoading: isStudentIdLoading } = useQuery({
    queryKey: ['student', user?.id],
    queryFn: () => studentService.getStudentByUserId(user?.id as string),
    enabled: !!user?.id,
  });

  // Fetch financial summary for current balance
  const { data: financialSummary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['student-financial-summary', studentId],
    queryFn: () => studentService.getStudentFinancialSummary(studentId as string),
    enabled: !!studentId,
  });

  // Fetch savings summary
  const { data: savingsSummary, isLoading: isSavingsLoading } = useQuery({
    queryKey: ['savings-summary', studentId],
    queryFn: () => savingsAccountService.getSavingsSummary(studentId as string),
    enabled: !!studentId,
  });

  // Fetch savings accounts list for tab count
  const { data: savingsAccounts = [] } = useQuery({
    queryKey: ['savings-accounts', studentId],
    queryFn: () => savingsAccountService.getStudentSavingsAccounts(studentId as string),
    enabled: !!studentId,
  });

  // Fetch rules
  const { data: rules = [], isLoading: isRulesLoading } = useQuery({
    queryKey: ['student-rules', studentId],
    queryFn: () => ruleEngineService.getStudentRules(studentId as string),
    enabled: !!studentId,
  });

  // Fetch pending transactions
  const { data: pendingTransactions = [], isLoading: isPendingLoading } = useQuery({
    queryKey: ['pending-transactions', studentId],
    queryFn: () => transactionService.getPendingTransactions(studentId as string),
    enabled: !!studentId,
  });

  useHeaderBreadcrumbs({
    breadcrumbs: [
      { label: 'Auto Rules & Savings', path: getBaseRoute('/financial-tools') },
      { label: 'Piggy Banks & Smart Rules' },
    ],
  });

  const currentBalance = Number(financialSummary?.currentBalance ?? 0);
  const totalSaved = Number(savingsSummary?.totalSaved ?? 0);
  const activeRulesCount = rules.filter((r) => r.is_active).length;
  const pendingCount = pendingTransactions.length;

  return (
    <Page title="Auto Rules & Savings | Gold Minds Portal">
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Header Welcome Ribbon */}
        <Box sx={{ mb: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'rgba(5, 150, 105, 0.12)',
                  color: '#059669',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon icon="solar:wallet-money-bold-duotone" width={24} height={24} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', fontSize: '1.375rem' }}>
                  Auto Rules & Savings Vaults
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                  Set up piggy banks for your goals and put your pocket money on smart auto-pilot.
                </Typography>
              </Box>
            </Stack>

            {/* Quick Navigation Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon icon="solar:card-2-bold-duotone" width={16} />}
              onClick={() => navigate(getBaseRoute('/street-cents'))}
              sx={{
                height: 36,
                borderRadius: 1.5,
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
              }}
            >
              View Ledger
            </Button>
          </Stack>
        </Box>

        {/* 4 High-Density KPI Metric Cards (Green & Slate) */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Main Wallet"
              value={`$D ${currentBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle="Pocket money available to spend"
              icon="solar:wallet-2-bold-duotone"
              isLoading={isStudentIdLoading || isSummaryLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Stashed"
              value={`$D ${totalSaved.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={`Saved across ${savingsSummary?.activeAccounts || 0} goal vaults`}
              icon="solar:wallet-money-bold-duotone"
              isLoading={isSavingsLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Smart Auto-Rules"
              value={`${activeRulesCount} Active`}
              subtitle="Automations running on auto-pilot"
              icon="solar:bolt-bold-duotone"
              isLoading={isRulesLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Pending Actions"
              value={`${pendingCount} Item${pendingCount !== 1 ? 's' : ''}`}
              subtitle={pendingCount === 0 ? 'All caught up' : 'Awaiting your review'}
              icon="solar:bell-bing-bold-duotone"
              isLoading={isPendingLoading}
            />
          </Grid>
        </Grid>

        {/* Tabbed Activity Container */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            overflow: 'hidden',
          }}
        >
          {/* Segmented Pill Tabs Navigation */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
            <Tabs
              value={activeTab}
              onChange={(_e, val) => setActiveTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': {
                  minHeight: 36,
                  py: 0.75,
                  px: 2,
                  borderRadius: 1.75,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  transition: 'all 0.15s ease-in-out',
                  mr: 1,
                  '&.Mui-selected': {
                    bgcolor: '#059669',
                    color: '#FFFFFF',
                    fontWeight: 700,
                  },
                  '&:hover:not(.Mui-selected)': {
                    bgcolor: 'rgba(5, 150, 105, 0.08)',
                    color: '#059669',
                  },
                },
              }}
            >
              <Tab
                icon={<Icon icon="solar:wallet-money-bold-duotone" width={16} />}
                iconPosition="start"
                label={`Piggy Banks & Goals (${savingsAccounts.length}/5)`}
              />
              <Tab
                icon={<Icon icon="solar:bolt-bold-duotone" width={16} />}
                iconPosition="start"
                label={`Smart Auto-Pilot Rules (${activeRulesCount} Active)`}
              />
              <Tab
                icon={<Icon icon="solar:shield-check-bold-duotone" width={16} />}
                iconPosition="start"
                label={pendingCount > 0 ? `Pending Reviews (${pendingCount})` : 'Pending Reviews'}
              />
            </Tabs>
          </Box>

          {/* Tab Workspaces */}
          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            {activeTab === 0 && (
              <SavingsAccountManager studentId={studentId as string} currentBalance={currentBalance} />
            )}
            {activeTab === 1 && (
              <TransactionRulesManager studentId={studentId as string} />
            )}
            {activeTab === 2 && (
              <PendingTransactionsPanel studentId={studentId as string} currentBalance={currentBalance} />
            )}
          </Box>
        </Card>
      </Container>
    </Page>
  );
}
